import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

const PULL_THRESHOLD = 70;
const MAX_PULL = 110;
const DAMPING = 0.5;

function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

function isStandalonePwa(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as any).standalone === true
  );
}

// iOS gives standalone (home-screen) PWAs no native pull-to-refresh gesture,
// so this re-implements it by hand and reloads the page once the pull passes
// the threshold. Scoped to iOS standalone only — everywhere else already has
// a native equivalent (or a browser chrome refresh button).
const PullToRefresh: React.FC = () => {
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const startYRef = useRef<number | null>(null);
  const pullingRef = useRef(false);
  const pullDistanceRef = useRef(0);
  const refreshingRef = useRef(false);

  useEffect(() => {
    if (!isIos() || !isStandalonePwa()) return;

    const getScrollTop = () =>
      document.scrollingElement?.scrollTop ?? window.scrollY;

    const reset = () => {
      pullingRef.current = false;
      startYRef.current = null;
      pullDistanceRef.current = 0;
      setPullDistance(0);
    };

    const onTouchStart = (e: TouchEvent) => {
      if (refreshingRef.current || getScrollTop() > 0) return;
      startYRef.current = e.touches[0].clientY;
      pullingRef.current = true;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (
        !pullingRef.current ||
        startYRef.current === null ||
        refreshingRef.current
      ) {
        return;
      }
      const delta = e.touches[0].clientY - startYRef.current;
      if (delta <= 0 || getScrollTop() > 0) {
        reset();
        return;
      }
      // Suppress the native scroll bounce while our own indicator is showing.
      e.preventDefault();
      const distance = Math.min(delta * DAMPING, MAX_PULL);
      pullDistanceRef.current = distance;
      setPullDistance(distance);
    };

    const onTouchEnd = () => {
      if (!pullingRef.current) return;
      pullingRef.current = false;
      startYRef.current = null;
      if (pullDistanceRef.current >= PULL_THRESHOLD) {
        refreshingRef.current = true;
        setRefreshing(true);
        setPullDistance(PULL_THRESHOLD);
        window.location.reload();
      } else {
        pullDistanceRef.current = 0;
        setPullDistance(0);
      }
    };

    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: false });
    document.addEventListener("touchend", onTouchEnd, { passive: true });
    document.addEventListener("touchcancel", onTouchEnd, { passive: true });

    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
      document.removeEventListener("touchcancel", onTouchEnd);
    };
  }, []);

  if (!isIos() || !isStandalonePwa()) return null;
  if (pullDistance === 0 && !refreshing) return null;

  const progress = Math.min(pullDistance / PULL_THRESHOLD, 1);

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-end",
        height: pullDistance,
        overflow: "hidden",
        zIndex: 2000,
        pointerEvents: "none",
        transition: refreshing ? "height 0.2s ease" : "none",
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          bgcolor: "background.paper",
          boxShadow: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 1,
        }}
      >
        {refreshing ? (
          <CircularProgress size={20} thickness={5} />
        ) : (
          <RefreshIcon
            sx={{
              fontSize: 20,
              color: "text.secondary",
              transform: `rotate(${progress * 360}deg)`,
              transition: "transform 0.05s linear",
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default PullToRefresh;
